<template>
  <div class="form-workspace">
    <div class="form-scroll">
      <div class="form-header">
        <div class="form-icon">{{ currentTool.icon }}</div>
        <div class="form-title-section">
        <h2 class="form-title">{{ currentTool.title }}</h2>
        <p class="form-subtitle" v-if="currentTool.subtitle">{{ currentTool.subtitle }}</p>
      </div>
    </div>

    <!-- ====== 条件式模板预览区 ====== -->
    <div class="template-preview" v-if="currentTool.showTemplatePreview">
      <!-- 表格模板 -->
      <template v-if="currentTool.templateType === 'table'">
        <div class="template-preview-head">
          <strong>{{ currentTool.templateTableTitle || '模板' }}</strong>
          <span>{{ currentTool.templateTableHint || '' }}</span>
        </div>
        <div class="template-table-wrap">
          <table>
            <thead>
              <tr>
                <th v-for="col in currentTool.templateTableColumns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in currentTool.templateTableRows" :key="ri">
                <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="template-actions" v-if="currentTool.templateActions">
          <button v-for="act in currentTool.templateActions" :key="(act.format || '') + (act.url || '')" type="button" @click="onTemplateAction(act)">{{ act.label }}</button>
          <span>{{ currentTool.templateActionHint || '' }}</span>
        </div>
        <div class="template-actions" v-else>
          <button type="button" @click="downloadTemplate('csv')">下载 CSV 模板</button>
          <button type="button" @click="copyTableTemplate">复制模板</button>
          <span>直接编辑上方文本区中的数据，或下载模板填写后粘贴。</span>
        </div>
      </template>
      <!-- pre 模板（默认） -->
      <template v-else>
        <div class="template-preview-head">
          <strong>论文降 AI 率英文正文示例模板</strong>
          <span>段落之间请保留空行</span>
        </div>
        <pre>{{ currentTool.templateContent }}</pre>
        <div class="template-actions">
          <button type="button" @click="downloadTemplate('word')">下载 Word 示例</button>
          <button type="button" @click="downloadTemplate('txt')">下载 TXT 示例</button>
          <button type="button" @click="copyTemplate">复制模板</button>
          <span>仅支持英文论文正文，删除图片、表格、参考文献和注释后上传。</span>
        </div>
      </template>
    </div>

    <div class="form-content">
      <!-- 无分组的字段（兼容旧配置） -->
      <template v-if="!currentTool.fieldGroups">
        <div v-for="(field, index) in currentTool.formFields" :key="index" class="form-field">
          <label class="field-label">
            {{ field.label }}<span class="field-required" v-if="field.required">*</span>
          </label>
          <input
            v-if="field.type === 'text' || field.type === 'number'"
            :type="field.type"
            class="field-input"
            :placeholder="field.placeholder"
            v-model="localFormData[field.key]"
            @input="updateLocalField(field.key, $event.target.value)"
          />
          <textarea
            v-else-if="field.type === 'textarea'"
            class="field-textarea"
            :placeholder="field.placeholder"
            :rows="field.rows || 4"
            :maxlength="field.maxlength"
            v-model="localFormData[field.key]"
            @input="updateLocalField(field.key, $event.target.value)"
          ></textarea>
          <select
            v-else-if="field.type === 'select'"
            class="field-select"
            v-model="localFormData[field.key]"
            @change="updateLocalField(field.key, $event.target.value)"
          >
            <option v-for="opt in field.options" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <div v-else-if="field.type === 'file'" class="field-upload">
            <input
              type="file"
              :accept="field.accept"
              @change="$emit('file-upload', field.key, $event)"
              :id="'file-' + field.key"
            />
            <label :for="'file-' + field.key" class="upload-btn" :class="{ 'upload-done': localFormData[field.key + '_name'], 'upload-loading': localFormData[field.key + '_uploading'] }">
              <span class="upload-icon" v-if="localFormData[field.key + '_uploading']">⏳</span>
              <span class="upload-icon" v-else-if="localFormData[field.key + '_name']">✅</span>
              <span class="upload-icon" v-else>📎</span>
              <span v-if="localFormData[field.key + '_uploading']">上传中...</span>
              <span v-else-if="localFormData[field.key + '_name']">{{ localFormData[field.key + '_name'] }}</span>
              <span v-else>{{ field.uploadText || '选择文件' }}</span>
            </label>
            <span class="upload-hint" v-if="field.hint && !localFormData[field.key + '_name'] && !localFormData[field.key + '_uploading']">{{ field.hint }}</span>
          </div>
          <p class="field-desc" v-if="field.desc">{{ field.desc }}</p>
        </div>
      </template>

      <!-- 有分组的字段 -->
      <template v-else>
        <!-- 第一组之前的无分组字段 -->
        <div v-for="field in ungroupedFields" :key="field.key" class="form-field">
          <label class="field-label">
            {{ field.label }}<span class="field-required" v-if="field.required">*</span>
          </label>
          <input
            v-if="field.type === 'text' || field.type === 'number'"
            :type="field.type"
            class="field-input"
            :placeholder="field.placeholder"
            v-model="localFormData[field.key]"
            @input="updateLocalField(field.key, $event.target.value)"
          />
          <textarea
            v-else-if="field.type === 'textarea'"
            class="field-textarea"
            :placeholder="field.placeholder"
            :rows="field.rows || 4"
            :maxlength="field.maxlength"
            v-model="localFormData[field.key]"
            @input="updateLocalField(field.key, $event.target.value)"
          ></textarea>
          <select
            v-else-if="field.type === 'select'"
            class="field-select"
            v-model="localFormData[field.key]"
            @change="updateLocalField(field.key, $event.target.value)"
          >
            <option v-for="opt in field.options" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <div v-else-if="field.type === 'file'" class="field-upload">
            <input
              type="file"
              :accept="field.accept"
              @change="$emit('file-upload', field.key, $event)"
              :id="'file-' + field.key"
            />
            <label :for="'file-' + field.key" class="upload-btn" :class="{ 'upload-done': localFormData[field.key + '_name'], 'upload-loading': localFormData[field.key + '_uploading'] }">
              <span class="upload-icon" v-if="localFormData[field.key + '_uploading']">⏳</span>
              <span class="upload-icon" v-else-if="localFormData[field.key + '_name']">✅</span>
              <span class="upload-icon" v-else>📎</span>
              <span v-if="localFormData[field.key + '_uploading']">上传中...</span>
              <span v-else-if="localFormData[field.key + '_name']">{{ localFormData[field.key + '_name'] }}</span>
              <span v-else>{{ field.uploadText || '选择文件' }}</span>
            </label>
            <span class="upload-hint" v-if="field.hint && !localFormData[field.key + '_name'] && !localFormData[field.key + '_uploading']">{{ field.hint }}</span>
          </div>
        </div>

        <!-- 分组字段 -->
        <div v-for="(groupConfig, groupKey) in currentTool.fieldGroups" :key="groupKey">
          <template v-if="groupConfig.title">
            <div class="form-group">
              <h3 class="group-title">{{ groupConfig.title }}</h3>
              <div class="group-fields" :style="{ gridTemplateColumns: 'repeat(' + (groupConfig.cols || 1) + ', 1fr)' }">
                <div v-for="field in groupedFields[groupKey]" :key="field.key" class="form-field group-field">
                  <label class="field-label">
                    {{ field.label }}<span class="field-required" v-if="field.required">*</span>
                  </label>
                  <input v-if="field.type === 'text' || field.type === 'number'" :type="field.type" class="field-input" :placeholder="field.placeholder" v-model="localFormData[field.key]" @input="updateLocalField(field.key, $event.target.value)" />
                  <textarea v-else-if="field.type === 'textarea'" class="field-textarea" :placeholder="field.placeholder" :rows="field.rows || 4" :maxlength="field.maxlength" v-model="localFormData[field.key]" @input="updateLocalField(field.key, $event.target.value)" ></textarea>
                  <select v-else-if="field.type === 'select'" class="field-select" v-model="localFormData[field.key]" @change="updateLocalField(field.key, $event.target.value)">
                    <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <div v-else-if="field.type === 'file'" class="field-upload">
                    <input type="file" :accept="field.accept" @change="$emit('file-upload', field.key, $event)" :id="'file-' + field.key" />
                    <label :for="'file-' + field.key" class="upload-btn" :class="{ 'upload-done': localFormData[field.key + '_name'], 'upload-loading': localFormData[field.key + '_uploading'] }">
                      <span class="upload-icon" v-if="localFormData[field.key + '_uploading']">⏳</span>
                      <span class="upload-icon" v-else-if="localFormData[field.key + '_name']">✅</span>
                      <span class="upload-icon" v-else>📎</span>
                      <span v-if="localFormData[field.key + '_uploading']">上传中...</span>
                      <span v-else-if="localFormData[field.key + '_name']">{{ localFormData[field.key + '_name'] }}</span>
                      <span v-else>{{ field.uploadText || '选择文件' }}</span>
                    </label>
                    <span class="upload-hint" v-if="field.hint && !localFormData[field.key + '_name'] && !localFormData[field.key + '_uploading']">{{ field.hint }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="form-field-row" :style="{ gridTemplateColumns: 'repeat(' + (groupConfig.cols || 1) + ', 1fr)' }">
              <div v-for="field in groupedFields[groupKey]" :key="field.key" class="form-field">
                <label class="field-label">
                  {{ field.label }}<span class="field-required" v-if="field.required">*</span>
                </label>
                <input v-if="field.type === 'text' || field.type === 'number'" :type="field.type" class="field-input" :placeholder="field.placeholder" v-model="localFormData[field.key]" @input="updateLocalField(field.key, $event.target.value)" />
                <textarea v-else-if="field.type === 'textarea'" class="field-textarea" :placeholder="field.placeholder" :rows="field.rows || 4" :maxlength="field.maxlength" v-model="localFormData[field.key]" @input="updateLocalField(field.key, $event.target.value)" ></textarea>
                <select v-else-if="field.type === 'select'" class="field-select" v-model="localFormData[field.key]" @change="updateLocalField(field.key, $event.target.value)">
                  <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <div v-else-if="field.type === 'file'" class="field-upload">
                  <input type="file" :accept="field.accept" @change="$emit('file-upload', field.key, $event)" :id="'file-' + field.key" />
                  <label :for="'file-' + field.key" class="upload-btn" :class="{ 'upload-done': localFormData[field.key + '_name'], 'upload-loading': localFormData[field.key + '_uploading'] }">
                    <span class="upload-icon" v-if="localFormData[field.key + '_uploading']">⏳</span>
                    <span class="upload-icon" v-else-if="localFormData[field.key + '_name']">✅</span>
                    <span class="upload-icon" v-else>📎</span>
                    <span v-if="localFormData[field.key + '_uploading']">上传中...</span>
                    <span v-else-if="localFormData[field.key + '_name']">{{ localFormData[field.key + '_name'] }}</span>
                    <span v-else>{{ field.uploadText || '选择文件' }}</span>
                  </label>
                  <span class="upload-hint" v-if="field.hint && !localFormData[field.key + '_name'] && !localFormData[field.key + '_uploading']">{{ field.hint }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
    </div>
    <div class="form-actions">
      <button
        class="submit-btn"
        :style="{ background: currentTool.btnColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }"
        @click="$emit('submit', localFormData)"
        :disabled="isSubmitting"
      >
        <span v-if="!isSubmitting">{{ currentTool.btnText || '立即提交' }}</span>
        <span v-else>处理中...</span>
      </button>
    </div>

    <!-- ====== 完整结果展示页（独立组件，仅在任务完成且拿到真实结果时显示）====== -->
    <!-- :key 强制 markdown 变化时整个组件重挂载，确保 keywords / renderMarkdown 用最新代码重新计算 -->
    <!-- 【2026-06-18 改造】按 currentTool.resultPageType 切换组件：'simple' → 简洁结果页（无统计卡/文献表/综合分析等），纯 markdown 渲染 -->
    <!--   现用于 claritone-reader (paper_qa) 和 writing (survey) 两个工具 -->
    <!--   未来其他"纯 markdown 输出"工具也可设置 resultPageType: 'simple' 复用此组件 -->
    <!-- 注意：v-if / v-else-if 必须是连续的兄弟节点，v-else-if 不能用在独立的 v-if 之后 -->
    <ReaderResultPage
      v-if="showResultPage && currentTool.resultPageType === 'simple'"
      :key="resultMarkdown + '|' + (resultTitle || '')"
      :title="resultTitle"
      :elapsed-ms="resultElapsedMs"
      :markdown="resultMarkdown"
      :show-title-card="currentTool.showTitleCard || false"
      :card-title="currentTool.titleCardTitle || resultTitle"
      :meta-line="currentTool.titleCardMeta || ''"
      :error-message="resultErrorDetail"
      :copy-text="resultCopyText"
      :docx-base64="resultDocxBase64"
      :docx-filename="resultDocxFilename"
    />
    <ResultPage
      v-else-if="showResultPage"
      :key="resultMarkdown + '|' + (resultTitle || '') + '|' + (resultSummary && Object.keys(resultSummary).length)"
      :title="resultTitle"
      :tool-id="resultToolId"
      :elapsed-ms="resultElapsedMs"
      :markdown="resultMarkdown"
      :copy-text="resultCopyText"
      :docx-base64="resultDocxBase64"
      :docx-filename="resultDocxFilename"
      :summary="resultSummary"
    />

    <div class="result-card" :class="{ 'is-running': isSubmitting || progressVisible }" v-if="isSubmitting || progressVisible">
      <!-- 顶部栏：左标题 + 右状态 -->
      <div class="result-header">
        <span class="result-title">运行结果</span>
        <span class="result-status">
          {{ isSubmitting || progressVisible ? '处理中' : '已完成' }}<template v-if="resultDuration">({{ resultDuration }})</template>
        </span>
      </div>
      <!-- 状态行：空心圆 + 步骤文字 -->
      <div class="result-row">
        <span class="result-circle" :class="{ spinning: isSubmitting || progressVisible }"></span>
        <span class="result-text">{{ resultStepText || '正在解析检索主题' }}</span>
      </div>
      <!-- 进度条 -->
      <div class="result-progress-track">
        <div
          class="result-progress-fill"
          :class="{ 'fill-idle': !isSubmitting && !progressVisible }"
          :style="{ width: (isSubmitting || progressVisible ? (progressPercent || 0) : 100) + '%' }"
        ></div>
      </div>
      <!-- 描述行 — 按工具配置（writing 用 loading3.png 文案；其他工具用通用文案） -->
      <div class="result-desc">{{ currentTool.progressDescText || '正在整理输入内容和运行参数。' }}</div>
      <!-- 底注 — 按工具配置 -->
      <div class="result-footer">{{ currentTool.progressFooterText || '任务正在处理中，请保持本页打开。' }}</div>
    </div>

    <p class="form-footer-note" v-if="currentTool.footerNote">{{ currentTool.footerNote }}</p>
    </div>
  </div>
</template>

<script>
import ResultPage from './result/ResultPage.vue'
import ReaderResultPage from './result/ReaderResultPage.vue'

export default {
  name: 'FormWorkspace',
  components: {
    ResultPage,
    ReaderResultPage
  },
  props: {
    currentTool: {
      type: Object,
      required: true
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    // 进度输出相关
    progressVisible: {
      type: Boolean,
      default: false
    },
    progressTitle: {
      type: String,
      default: ''
    },
    progressSubtitle: {
      type: String,
      default: ''
    },
    progressPercent: {
      type: Number,
      default: 0
    },
    progressSteps: {
      type: Array,
      default: () => []
    },
    activeStep: {
      type: Number,
      default: 0
    },
    // 运行结果卡片专用
    resultDuration: {
      type: String,
      default: ''
    },
    resultStepText: {
      type: String,
      default: ''
    },
    // 完整结果页（独立组件 ResultPage）显示控制
    showResultPage: {
      type: Boolean,
      default: false
    },
    // 透传给 ResultPage 的真实结果数据
    resultTitle: { type: String, default: '' },
    resultToolId: { type: String, default: '' },
    resultElapsedMs: { type: Number, default: 0 },
    resultMarkdown: { type: String, default: '' },
    resultCopyText: { type: String, default: '' },
    resultDocxBase64: { type: String, default: '' },
    resultDocxFilename: { type: String, default: '' },
    resultSummary: { type: Object, default: () => ({}) },
    resultErrorDetail: { type: String, default: '' },
    resultErrorDetail: { type: String, default: '' },
    resultErrorDetail: { type: String, default: '' }
  },
  data() {
    return {
      localFormData: {}
    }
  },
  computed: {
    // 无分组的字段（group 为空的字段）
    ungroupedFields() {
      if (!this.currentTool.formFields) return []
      return this.currentTool.formFields.filter(f => !f.group)
    },
    // 按 group 分组的字段
    groupedFields() {
      if (!this.currentTool.formFields || !this.currentTool.fieldGroups) return {}
      const groups = {}
      Object.keys(this.currentTool.fieldGroups).forEach(key => {
        groups[key] = this.currentTool.formFields.filter(f => f.group === key)
      })
      return groups
    }
  },
  watch: {
    formData: {
      handler(val) {
        this.localFormData = { ...val }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    updateLocalField(key, value) {
      this.localFormData[key] = value
      this.$emit('form-update', this.localFormData)
    },
    downloadTemplate(format) {
      this.$emit('download-template', format)
    },
    // 【2026-06-18 新增】templateAction 路由：有 url 直接下载，无 url 走原 downloadTemplate 事件
    onTemplateAction(act) {
      if (act && act.url) {
        // 直接走 <a> 标签下载（浏览器自动从 url 推断文件名）
        const a = document.createElement('a')
        a.href = act.url
        a.download = ''
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        this.downloadTemplate(act.format)
      }
    },
    copyTableTemplate() {
      const cols = this.currentTool.templateTableColumns || []
      const rows = this.currentTool.templateTableRows || []
      const lines = [cols.join('\t')]
      rows.forEach(r => lines.push(r.join('\t')))
      const content = lines.join('\n')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(content).then(() => {
          alert('表格模板已复制到剪贴板（制表符分隔）')
        }).catch(() => {
          alert('复制失败，请手动复制')
        })
      } else {
        alert('复制失败，请手动复制')
      }
    },
    copyTemplate() {
      const content = this.currentTool.templateContent || ''
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(content).then(() => {
          alert('模板已复制到剪贴板')
        }).catch(() => {
          alert('复制失败，请手动复制')
        })
      } else {
        alert('复制失败，请手动复制')
      }
    }
  }
}
</script>

<style scoped>
.form-workspace {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  /* 不限制高度、不内嵌滚动：让外层浏览器唯一滚动条接管 */
}

.form-scroll {
  width: 100%;
  /* 取消内部滚动，改用外层浏览器滚动 */
  overflow: visible;
}
.form-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8e8e8;
}
.form-icon {
  font-size: 48px;
}
.form-title-section {
  flex: 1;
}
.form-title {
  font-size: 28px;
  color: #1a1a2e;
  margin: 0 0 8px 0;
  font-weight: 600;
}
.form-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.6;
}

/* ====== 模板预览区（对齐截图：浅灰背景 + 圆角边框）====== */
.template-preview {
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}
.template-preview-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f4f4f4;
  border-bottom: 1px solid #e5e5e5;
}
.template-preview-head strong {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
}
.template-preview-head span {
  font-size: 12px;
  color: #999;
}
.template-table-wrap {
  overflow-x: auto;
  background: #fff;
  border-radius: 0 0 8px 8px;
}
.template-table-wrap table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border-spacing: 0;
}
.template-table-wrap th {
  padding: 12px 16px;
  text-align: left;
  background: #f0f4f8;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
  border-bottom: 2px solid #dce3ec;
  letter-spacing: 0.3px;
}
.template-table-wrap td {
  padding: 11px 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  color: #444;
  font-size: 13px;
  transition: background 0.15s ease;
}
.template-table-wrap tbody tr:nth-child(even) td {
  background: #f9fbfd;
}
.template-table-wrap tbody tr:hover td {
  background: #eef4fb;
}

.template-preview pre {
  margin: 0;
  padding: 16px 18px;
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.9;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
}
.template-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  flex-wrap: wrap;
}
.template-actions button {
  padding: 6px 14px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.15s ease;
}
.template-actions button:hover {
  border-color: #999;
  color: #111;
}
.template-actions span {
  font-size: 12px;
  color: #888;
  margin-left: auto;
}

/* ====== 表单字段 ====== */
.form-content {
  margin-bottom: 32px;
}
.form-field {
  margin-bottom: 24px;
}
.field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a2e;
  margin-bottom: 8px;
}
.field-required {
  color: #e74c3c;
  margin-left: 4px;
}
.field-input,
.field-textarea,
.field-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;
  box-sizing: border-box;
}
.field-input:focus,
.field-textarea:focus,
.field-select:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}
.field-textarea {
  resize: vertical;
  min-height: 100px;
}
.field-desc {
  font-size: 12px;
  color: #999;
  margin: 8px 0 0 0;
}
.field-upload {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.field-upload input[type="file"] {
  display: none;
}
.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 2px dashed #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.3s ease;
}
.upload-btn:hover {
  border-color: #667eea;
  color: #667eea;
}
.upload-icon {
  font-size: 18px;
}
.upload-hint {
  font-size: 12px;
  color: #999;
}
.upload-loading {
  border-color: #667eea !important;
  color: #667eea !important;
  background: #f0f4ff !important;
}
.upload-done {
  border-style: solid !important;
  border-color: #52c41a !important;
  color: #52c41a !important;
  background: #f6ffed !important;
}
.form-actions {
  padding-top: 24px;
  border-top: 1px solid #e8e8e8;
}
.submit-btn {
  padding: 14px 48px;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}
.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* ====== 运行结果卡片 — 对齐 loading2.png 浅色面板 ====== */
.result-card {
  margin-top: 16px;
  border-radius: 10px;
  overflow: hidden;
  background: #f7f7f7;
  border: 1px solid #ececec;
  font-size: 14px;
  color: #1a1a1a;
}

/* 顶部栏 — 白底，左标题右状态 */
.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #e8e8e8;
  border-bottom: 1px solid #ececec;
}
.result-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}
.result-status {
  font-size: 12px;
  color: #999;
}

/* 状态行 — 圆环 loader + 阶段文字 */
.result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 8px 18px;
}
.result-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2.5px solid #6b7280;
  border-top-color: transparent;
  display: inline-block;
  flex-shrink: 0;
  opacity: 0.85;
}
.result-circle.spinning {
  border-top-color: #6b7280;
  opacity: 1;
  animation: circleSpin 0.9s linear infinite;
}
@keyframes circleSpin {
  to { transform: rotate(360deg); }
}
.result-text {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
}

/* 进度条 — 深绿单色 */
.result-progress-track {
  margin: 4px 18px 10px 18px;
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
}
.result-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: #2d6a4f;
  transition: width 0.5s ease;
}
.result-progress-fill.fill-idle {
  background: #2d6a4f;
  opacity: 0.85;
}

/* 描述行 */
.result-desc {
  padding: 4px 18px 8px 18px;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
}

/* 底注 */
.result-footer {
  padding: 10px 18px 14px 18px;
  font-size: 12.5px;
  color: #9ca3af;
}

.form-footer-note {
  font-size: 13px;
  color: #999;
  margin-top: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  line-height: 1.6;
}

/* 分组样式 */
.form-group {
  margin-bottom: 24px;
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
}
.form-field-row {
  display: grid;
  gap: 20px;
  margin-bottom: 24px;
}
.group-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}
.group-fields {
  display: grid;
  gap: 16px;
}
.group-field {
  margin-bottom: 0;
}
.group-field .field-label {
  font-size: 13px;
  margin-bottom: 6px;
}
.group-field .field-input,
.group-field .field-select {
  padding: 10px 12px;
  font-size: 13px;
}
@media (max-width: 768px) {
  .form-workspace {
    padding: 20px 16px;
  }
  .form-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .form-title {
    font-size: 22px;
  }
  .template-preview-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .template-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .template-actions span {
    margin-left: 0;
    margin-top: 6px;
  }
  .field-upload {
    flex-direction: column;
    align-items: flex-start;
  }
  .group-fields {
    grid-template-columns: 1fr !important;
  }
  .form-group {
    padding: 16px;
  }
}

</style>
